/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable quotes */
import { CommonModule } from "@angular/common";
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  OnDestroy,
  Output,
  inject,
  DestroyRef,
} from "@angular/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { DialogModule } from "primeng/dialog";
import { ButtonModule } from "primeng/button";
import { InputTextModule } from "primeng/inputtext";
import { TooltipModule } from "primeng/tooltip";
import { CheckboxModule } from "primeng/checkbox";
import { EditorComponent as MonacoEditorComponent } from "ngx-monaco-editor-v2";
import {
  CustomComponent,
  CustomComponentFormData,
} from "../../../../interfaces/custom-component.interface";
import { DetailedSummaryComponent } from "../../../../interfaces/customer-portal-settings.interface";

interface DeviceSize {
  name: string;
  icon: string;
  width: string;
}

interface EditorForm {
  name: FormControl<string>;
  htmlContent: FormControl<string>;
  cssContent: FormControl<string>;
  enabled: FormControl<boolean>;
  position: FormControl<number>;
}

@Component({
  selector: "app-custom-component-editor",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    TooltipModule,
    CheckboxModule,
    MonacoEditorComponent,
  ],
  template: `
    <p-dialog
      [(visible)]="visible"
      [modal]="true"
      [draggable]="false"
      [resizable]="false"
      [maximizable]="true"
      [style]="{ width: '98vw', height: '95vh', maxWidth: '1800px' }"
      [contentStyle]="{ height: 'calc(95vh - 70px)', padding: 0 }"
      [header]="
        isEditMode ? 'Edit Custom Component' : 'Create Custom Component'
      "
      (onHide)="onCancel()"
      styleClass="fullscreen-editor-dialog compact-dialog"
    >
      <div class="split-layout" [class.preview-hidden]="!showMobilePreview">
        <!-- LEFT PANEL: Configuration -->
        <div class="config-panel">
          <form [formGroup]="form" class="config-form">
            <!-- Header Section - Compact -->
            <div class="config-section config-header">
              <div class="config-header-row">
                <h3 class="section-title">
                  <i class="pi pi-cog"></i>
                  Configuration
                </h3>
                <!-- Mobile Preview Toggle Button -->
                <button
                  type="button"
                  class="mobile-preview-toggle"
                  (click)="showMobilePreview = !showMobilePreview"
                >
                  <i
                    class="pi"
                    [class.pi-eye]="!showMobilePreview"
                    [class.pi-eye-slash]="showMobilePreview"
                  ></i>
                  {{ showMobilePreview ? "Hide" : "Show" }} Preview
                </button>
              </div>

              <!-- Component Name & Visibility Row -->
              <div class="field-row compact name-visibility-row">
                <div class="field flex-1 compact">
                  <label for="name" class="field-label">
                    Component Name
                    <span class="required">*</span>
                  </label>
                  <input
                    pInputText
                    id="name"
                    formControlName="name"
                    placeholder="e.g., Promotional Banner"
                    class="w-full input-sm"
                  />
                  @if (
                    form.controls.name.invalid && form.controls.name.touched
                  ) {
                    <small class="p-error"
                      >Name is required (max 100 characters)</small
                    >
                  }
                </div>

                <!-- Visibility Toggle -->
                <div class="visibility-toggle">
                  <label class="field-label">Visibility</label>
                  <div class="visibility-control">
                    <p-checkbox
                      formControlName="enabled"
                      [binary]="true"
                      inputId="enabled"
                    />
                    <label for="enabled" class="visibility-label">
                      @if (form.controls.enabled.value) {
                        <span class="status-text enabled">
                          <i class="pi pi-eye"></i>
                          Visible
                        </span>
                      } @else {
                        <span class="status-text disabled">
                          <i class="pi pi-eye-slash"></i>
                          Hidden
                        </span>
                      }
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <!-- HTML Editor Section -->
            <div class="config-section editor-section">
              <label class="field-label editor-label">
                <i class="pi pi-code"></i>
                HTML Content
                <span class="required">*</span>
                <i
                  class="pi pi-info-circle info-icon"
                  pTooltip="JavaScript is supported! Scripts run safely in a sandboxed iframe."
                  tooltipPosition="right"
                ></i>
              </label>
              <div class="monaco-container html-editor">
                <ngx-monaco-editor
                  [options]="htmlEditorOptions"
                  [ngModel]="form.controls.htmlContent.value"
                  (ngModelChange)="form.controls.htmlContent.setValue($event)"
                  [ngModelOptions]="{ standalone: true }"
                />
              </div>
              @if (
                form.controls.htmlContent.invalid &&
                form.controls.htmlContent.touched
              ) {
                <small class="p-error">HTML content is required</small>
              }
            </div>

            <!-- CSS Editor Section -->
            <div class="config-section editor-section">
              <label class="field-label editor-label">
                <i class="pi pi-palette"></i>
                CSS Styles (optional)
              </label>
              <div class="monaco-container css-editor">
                <ngx-monaco-editor
                  [options]="cssEditorOptions"
                  [ngModel]="form.controls.cssContent.value"
                  (ngModelChange)="form.controls.cssContent.setValue($event)"
                  [ngModelOptions]="{ standalone: true }"
                />
              </div>
            </div>
          </form>
        </div>

        <!-- RIGHT PANEL: Preview -->
        <div class="preview-panel" [class.mobile-visible]="showMobilePreview">
          <div class="preview-header">
            <div class="preview-header-left">
              <!-- Mobile Back Button -->
              <button
                type="button"
                class="mobile-back-btn"
                (click)="showMobilePreview = false"
              >
                <i class="pi pi-arrow-left"></i>
                Back
              </button>
              <h3 class="section-title">
                <i class="pi pi-eye"></i>
                Live Preview
              </h3>
              <!-- Device Size Toggles -->
              <div class="device-toggles">
                @for (device of deviceSizes; track device.name) {
                  <button
                    type="button"
                    class="device-btn"
                    [class.active]="selectedDevice === device.name"
                    (click)="selectedDevice = device.name"
                    [pTooltip]="device.name"
                    tooltipPosition="bottom"
                  >
                    <i class="pi" [ngClass]="device.icon"></i>
                  </button>
                }
              </div>
            </div>
            <p-button
              label="Refresh"
              icon="pi pi-refresh"
              [text]="true"
              size="small"
              (click)="updatePreview()"
              styleClass="refresh-btn"
            />
          </div>

          <div class="preview-content-wrapper">
            @if (previewSrcdoc) {
              <div
                class="preview-frame-container"
                [style.maxWidth]="getDeviceWidth()"
              >
                <iframe
                  class="preview-iframe"
                  [srcdoc]="previewSrcdoc"
                  sandbox="allow-scripts"
                  title="Component Preview"
                  (load)="onPreviewIframeLoad($event)"
                ></iframe>
              </div>
            } @else {
              <div class="preview-placeholder">
                <i class="pi pi-image"></i>
                <span>Enter HTML to see preview</span>
              </div>
            }
          </div>

          <div class="security-note">
            <i class="pi pi-check-circle"></i>
            <span>
              JavaScript runs in a secure sandbox. Scripts cannot access the
              parent page, cookies, or localStorage.
            </span>
          </div>
        </div>
      </div>

      <ng-template pTemplate="footer">
        <div class="dialog-footer">
          <p-button
            label="Cancel"
            [text]="true"
            severity="secondary"
            size="small"
            (click)="onCancel()"
            styleClass="btn-cancel"
          />
          <p-button
            [label]="isEditMode ? 'Save Changes' : 'Create Component'"
            icon="pi pi-check"
            size="small"
            (click)="onSave()"
            [disabled]="form.invalid"
            styleClass="btn-save"
          />
        </div>
      </ng-template>
    </p-dialog>
  `,
  styles: [
    `
      /* Dialog Overrides - Compact Header */
      :host ::ng-deep .fullscreen-editor-dialog {
        .p-dialog-content {
          padding: 0 !important;
          overflow: hidden;
        }

        .p-dialog-header {
          padding: 0.625rem 1rem !important;
          border-bottom: 1px solid #e5e7eb;
        }

        .p-dialog-header-icons {
          gap: 0.25rem;
        }

        .p-dialog-header-icon {
          width: 1.75rem;
          height: 1.75rem;
        }

        .p-dialog-footer {
          padding: 0.5rem 1rem !important;
          border-top: 1px solid #e5e7eb;
        }
      }

      /* Maximized dialog - fill entire screen */
      :host ::ng-deep .fullscreen-editor-dialog.p-dialog-maximized {
        width: 100vw !important;
        height: 100vh !important;
        max-width: 100vw !important;
        max-height: 100vh !important;
        top: 0 !important;
        left: 0 !important;
        margin: 0 !important;
        border-radius: 0 !important;

        .p-dialog-content {
          height: calc(100vh - 110px) !important;
        }
      }

      .split-layout {
        display: flex;
        height: 100%;
        gap: 0;
      }

      /* LEFT PANEL - Configuration */
      .config-panel {
        flex: 1;
        display: flex;
        flex-direction: column;
        border-right: 1px solid #e5e7eb;
        background: #fafafa;
        overflow: hidden;
      }

      .config-form {
        display: flex;
        flex-direction: column;
        height: 100%;
        overflow: hidden;
      }

      .config-section {
        padding: 0.75rem 1rem;
        border-bottom: 1px solid #e5e7eb;
      }

      .config-section.config-header {
        padding: 0.625rem 1rem;
      }

      .config-section.editor-section {
        flex: 1;
        display: flex;
        flex-direction: column;
        min-height: 0;
        overflow: hidden;
        padding: 0.5rem 1rem;
      }

      .config-section.editor-section:last-child {
        border-bottom: none;
      }

      .section-title {
        font-size: 0.8125rem;
        font-weight: 600;
        color: #374151;
        margin: 0 0 0.5rem 0;
        display: flex;
        align-items: center;
        gap: 0.375rem;
      }

      .section-title i {
        color: #6b7280;
        font-size: 0.875rem;
      }

      /* Config Header Row */
      .config-header-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 0.5rem;
      }

      .config-header-row .section-title {
        margin-bottom: 0;
      }

      /* Mobile Preview Toggle - Hidden by default on desktop */
      .mobile-preview-toggle {
        display: none;
      }

      /* Compact Field Styles */
      .field {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
      }

      .field:last-child {
        margin-bottom: 0;
      }

      .field-row {
        display: flex;
        gap: 0.75rem;
        align-items: flex-start;
      }

      .field-row.compact {
        gap: 0.5rem;
      }

      .field-label {
        font-weight: 500;
        font-size: 0.75rem;
        color: #374151;
        display: flex;
        align-items: center;
        gap: 0.25rem;
      }

      .editor-label {
        margin-bottom: 0.25rem;
      }

      .field-label i.pi-code,
      .field-label i.pi-palette {
        color: #6366f1;
      }

      .info-icon {
        color: #9ca3af;
        font-size: 0.75rem;
        cursor: help;
      }

      .required {
        color: #ef4444;
      }

      /* Compact Input Styles */
      :host ::ng-deep .input-sm {
        padding: 0.375rem 0.625rem !important;
        font-size: 0.8125rem !important;
      }

      /* Name & Visibility Row */
      .name-visibility-row {
        align-items: flex-end;
      }

      /* Enhanced Visibility Toggle */
      .visibility-toggle {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
        flex-shrink: 0;
      }

      .visibility-control {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.375rem 0.625rem;
        background: #f3f4f6;
        border-radius: 6px;
        border: 1px solid #e5e7eb;
        height: 34px; /* Match input height */
      }

      .visibility-label {
        cursor: pointer;
      }

      .status-text {
        display: flex;
        align-items: center;
        gap: 0.375rem;
        font-size: 0.75rem;
        font-weight: 500;
        white-space: nowrap;
      }

      .status-text.enabled {
        color: #059669;
      }

      .status-text.enabled i {
        color: #10b981;
      }

      .status-text.disabled {
        color: #6b7280;
      }

      .status-text.disabled i {
        color: #9ca3af;
      }

      /* Device Size Toggles */
      .device-toggles {
        display: flex;
        gap: 0.25rem;
        margin-left: 0.75rem;
        padding-left: 0.75rem;
        border-left: 1px solid #e5e7eb;
      }

      .device-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 1.75rem;
        height: 1.75rem;
        padding: 0;
        background: transparent;
        border: 1px solid transparent;
        border-radius: 4px;
        color: #9ca3af;
        cursor: pointer;
        transition: all 0.15s ease;
      }

      .device-btn:hover {
        color: #6366f1;
        background: #f3f4f6;
      }

      .device-btn.active {
        color: #6366f1;
        background: #eef2ff;
        border-color: #c7d2fe;
      }

      .device-btn i {
        font-size: 0.875rem;
      }

      /* Preview Frame Container */
      .preview-frame-container {
        background: white;
        border: 1px solid #e5e7eb;
        border-radius: 6px;
        overflow: hidden;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        transition: max-width 0.3s ease;
        height: 100%;
        width: 100%;
        display: flex;
        flex-direction: column;
      }

      .preview-iframe {
        width: 100%;
        border: none;
        display: block;
        flex: 1;
        min-height: 0;
      }

      /* Monaco Editor Containers */
      .monaco-container {
        flex: 1;
        min-height: 0;
        overflow: hidden;
        margin-top: 0.25rem;
      }

      .html-editor {
        flex: 2;
      }

      .css-editor {
        flex: 1;
      }

      :host ::ng-deep .monaco-container ngx-monaco-editor {
        height: 100% !important;
      }

      :host ::ng-deep .monaco-container .monaco-editor {
        border-radius: 4px;
      }

      /* Hide Monaco's accessibility widgets that can cause issues */
      :host ::ng-deep .monaco-editor .accessibilityHelpWidget,
      :host ::ng-deep .monaco-editor .iPadShowKeyboard {
        display: none !important;
      }

      /* RIGHT PANEL - Preview */
      .preview-panel {
        flex: 1;
        display: flex;
        flex-direction: column;
        background: #ffffff;
        overflow: hidden;
      }

      .preview-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0.5rem 1rem;
        border-bottom: 1px solid #e5e7eb;
        background: #f9fafb;
      }

      .preview-header-left {
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }

      .preview-header .section-title {
        margin: 0;
      }

      /* Mobile Back Button - Hidden by default on desktop */
      .mobile-back-btn {
        display: none;
      }

      :host ::ng-deep .refresh-btn {
        font-size: 0.75rem !important;
        padding: 0.25rem 0.5rem !important;
      }

      .preview-content-wrapper {
        flex: 1;
        overflow: auto;
        padding: 0.5rem;
        background: #f3f4f6;
        min-height: 0;
        display: flex;
        flex-direction: column;
        align-items: center;
      }

      .preview-frame {
        background: white;
        border: 1px solid #e5e7eb;
        border-radius: 6px;
        padding: 1rem;
        min-height: 150px;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
      }

      .preview-rendered {
        width: 100%;
      }

      .preview-styles {
        display: none;
      }

      .preview-placeholder {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 200px;
        color: #9ca3af;
        gap: 0.75rem;
      }

      .preview-placeholder i {
        font-size: 2.5rem;
      }

      .preview-placeholder span {
        font-size: 0.8125rem;
      }

      .security-note {
        display: flex;
        align-items: center;
        gap: 0.375rem;
        padding: 0.5rem 1rem;
        font-size: 0.6875rem;
        color: #6b7280;
        background: #eff6ff;
        border-top: 1px solid #e5e7eb;
        line-height: 1.4;
      }

      .security-note i {
        color: #3b82f6;
        flex-shrink: 0;
      }

      /* Footer - Compact */
      .dialog-footer {
        display: flex;
        justify-content: flex-end;
        gap: 0.5rem;
      }

      :host ::ng-deep .btn-cancel {
        font-size: 0.8125rem !important;
        padding: 0.375rem 0.75rem !important;
      }

      :host ::ng-deep .btn-save {
        font-size: 0.8125rem !important;
        padding: 0.375rem 0.875rem !important;
      }

      /* ============================================
         MOBILE RESPONSIVENESS
         ============================================ */
      @media (max-width: 768px) {
        /* On mobile: show EITHER config OR preview (full screen each) */
        .split-layout {
          flex-direction: column;
        }

        /* Default: Config panel takes full height, preview hidden */
        .config-panel {
          flex: 1;
          border-right: none;
          border-bottom: none;
          overflow: auto;
        }

        /* Preview panel hidden by default on mobile */
        .preview-panel {
          display: none;
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 10;
          max-height: none;
          min-height: 0;
        }

        /* When preview is shown: hide config, show preview full screen */
        .preview-panel.mobile-visible {
          display: flex;
          position: relative;
          flex: 1;
        }

        .split-layout:has(.preview-panel.mobile-visible) .config-panel {
          display: none;
        }

        /* Mobile Preview Toggle Button */
        .mobile-preview-toggle {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.25rem 0.5rem;
          font-size: 0.625rem;
          font-weight: 500;
          color: #6366f1;
          background: #eef2ff;
          border: 1px solid #c7d2fe;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.15s ease;
          white-space: nowrap;
        }

        .mobile-preview-toggle:hover {
          background: #e0e7ff;
          border-color: #a5b4fc;
        }

        .mobile-preview-toggle:active {
          background: #c7d2fe;
        }

        .mobile-preview-toggle i {
          font-size: 0.625rem;
        }

        /* Mobile Back Button */
        .mobile-back-btn {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.25rem 0.5rem;
          font-size: 0.625rem;
          font-weight: 500;
          color: #374151;
          background: #f3f4f6;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.15s ease;
        }

        .mobile-back-btn:hover {
          background: #e5e7eb;
        }

        .mobile-back-btn i {
          font-size: 0.625rem;
        }

        /* Hide device toggles on mobile - not needed */
        .device-toggles {
          display: none;
        }

        /* Preview frame on mobile */
        .preview-frame-container {
          max-width: 100% !important;
        }

        /* More compact on mobile */
        .config-section {
          padding: 0.375rem 0.625rem;
        }

        .config-section.config-header {
          padding: 0.375rem 0.625rem;
        }

        .config-section.editor-section {
          padding: 0.25rem 0.625rem;
        }

        .config-header-row {
          margin-bottom: 0.375rem;
        }

        .section-title {
          font-size: 0.6875rem;
          margin-bottom: 0.25rem;
        }

        .field-label {
          font-size: 0.625rem;
        }

        .field.compact {
          margin-bottom: 0.25rem;
        }

        /* Stack name and visibility on mobile */
        .field-row {
          flex-direction: column;
          gap: 0.25rem;
        }

        .field-row .field {
          width: 100%;
        }

        .name-visibility-row {
          align-items: stretch;
        }

        .visibility-toggle {
          width: 100%;
        }

        .visibility-control {
          padding: 0.1875rem 0.375rem;
          height: auto;
        }

        .status-text {
          font-size: 0.625rem;
        }

        /* Smaller Monaco editors on mobile */
        .monaco-container {
          min-height: 80px;
          margin-top: 0.125rem;
        }

        .html-editor {
          flex: 2;
          min-height: 100px;
        }

        .css-editor {
          flex: 1;
          min-height: 60px;
        }

        /* Compact preview */
        .preview-header {
          padding: 0.25rem 0.625rem;
        }

        .preview-content-wrapper {
          padding: 0.375rem;
        }

        .preview-frame {
          padding: 0.5rem;
          min-height: 80px;
        }

        .preview-placeholder {
          height: 100px;
          gap: 0.375rem;
        }

        .preview-placeholder i {
          font-size: 1.5rem;
        }

        .preview-placeholder span {
          font-size: 0.6875rem;
        }

        .security-note {
          padding: 0.25rem 0.625rem;
          font-size: 0.5625rem;
        }

        /* Smaller inputs on mobile */
        :host ::ng-deep .input-sm {
          padding: 0.25rem 0.5rem !important;
          font-size: 0.75rem !important;
        }

        /* Much smaller buttons on mobile */
        :host ::ng-deep .btn-cancel {
          font-size: 0.625rem !important;
          padding: 0.125rem 0.375rem !important;
          min-height: unset !important;
          height: auto !important;
        }

        :host ::ng-deep .btn-save {
          font-size: 0.625rem !important;
          padding: 0.125rem 0.5rem !important;
          min-height: unset !important;
          height: auto !important;
        }

        :host ::ng-deep .btn-save .p-button-icon {
          font-size: 0.5625rem !important;
        }

        :host ::ng-deep .btn-cancel .p-button-label,
        :host ::ng-deep .btn-save .p-button-label {
          font-size: 0.625rem !important;
        }

        :host ::ng-deep .refresh-btn {
          font-size: 0.625rem !important;
          padding: 0.125rem 0.25rem !important;
        }

        /* Compact dialog footer */
        .dialog-footer {
          gap: 0.25rem;
        }

        /* Compact dialog header on mobile */
        :host ::ng-deep .fullscreen-editor-dialog .p-dialog-header {
          padding: 0.5rem 0.75rem !important;
        }

        :host ::ng-deep .fullscreen-editor-dialog .p-dialog-title {
          font-size: 0.875rem !important;
        }

        :host ::ng-deep .fullscreen-editor-dialog .p-dialog-footer {
          padding: 0.375rem 0.75rem !important;
        }
      }

      /* Extra small screens */
      @media (max-width: 480px) {
        .config-section {
          padding: 0.25rem 0.5rem;
        }

        .config-section.config-header {
          padding: 0.25rem 0.5rem;
        }

        .config-section.editor-section {
          padding: 0.1875rem 0.5rem;
        }

        .section-title {
          font-size: 0.625rem;
        }

        .field-label {
          font-size: 0.5625rem;
        }

        :host ::ng-deep .input-sm {
          padding: 0.1875rem 0.375rem !important;
          font-size: 0.6875rem !important;
        }

        .visibility-control {
          padding: 0.125rem 0.25rem;
        }

        .status-text {
          font-size: 0.5625rem;
        }

        .mobile-preview-toggle {
          font-size: 0.5625rem;
          padding: 0.1875rem 0.375rem;
        }

        .mobile-preview-toggle i {
          font-size: 0.5625rem;
        }

        /* Even smaller buttons on extra small screens */
        :host ::ng-deep .btn-cancel {
          font-size: 0.5625rem !important;
          padding: 0.0625rem 0.25rem !important;
          min-height: unset !important;
          height: auto !important;
        }

        :host ::ng-deep .btn-cancel .p-button-label {
          font-size: 0.5625rem !important;
        }

        :host ::ng-deep .btn-save {
          font-size: 0.5625rem !important;
          padding: 0.0625rem 0.375rem !important;
          min-height: unset !important;
          height: auto !important;
        }

        :host ::ng-deep .btn-save .p-button-label {
          font-size: 0.5625rem !important;
        }

        :host ::ng-deep .btn-save .p-button-icon {
          font-size: 0.5rem !important;
        }
      }
    `,
  ],
})
export class CustomComponentEditorComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  private readonly sanitizer = inject(DomSanitizer);

  // Store message handler reference for cleanup
  private previewMessageHandler?: (msg: MessageEvent) => void;

  @Input() visible = false;

  // Mobile preview toggle
  showMobilePreview = false;
  @Input() existingComponents: DetailedSummaryComponent[] = [];

  // Device size options for preview
  readonly deviceSizes: DeviceSize[] = [
    { name: "Mobile", icon: "pi-mobile", width: "375px" },
    { name: "Tablet", icon: "pi-tablet", width: "768px" },
    { name: "Desktop", icon: "pi-desktop", width: "100%" },
  ];
  selectedDevice = "Desktop";

  // Sandboxed iframe preview
  previewSrcdoc: SafeHtml | null = null;

  @Input() customComponents: CustomComponent[] = [];

  private _component: CustomComponent | null = null;
  @Input()
  set component(value: CustomComponent | null) {
    this._component = value;
    // When component changes and form exists, populate or reset it
    if (this.form) {
      if (value) {
        this.populateForm(value);
      } else {
        this.resetForm();
      }
    }
  }
  get component(): CustomComponent | null {
    return this._component;
  }

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() save = new EventEmitter<CustomComponentFormData>();
  @Output() cancelled = new EventEmitter<void>();

  form!: FormGroup<EditorForm>;

  // Monaco Editor options - Dark theme with enhanced features
  htmlEditorOptions = {
    theme: "vs-dark",
    language: "html",
    automaticLayout: true,
    wordWrap: "on" as const,
    fontSize: 13,
    lineNumbers: "on" as const,
    tabSize: 2,
    formatOnPaste: true,
    formatOnType: true,
    scrollBeyondLastLine: false,
    padding: { top: 8, bottom: 8 },

    // Visual enhancements
    minimap: { enabled: true, scale: 1, showSlider: "mouseover" as const },
    bracketPairColorization: { enabled: true },
    guides: {
      bracketPairs: true,
      indentation: true,
      highlightActiveIndentation: true,
    },
    renderLineHighlight: "all" as const,
    renderWhitespace: "selection" as const,
    smoothScrolling: true,
    cursorBlinking: "smooth" as const,
    cursorSmoothCaretAnimation: "on" as const,
    colorDecorators: true,
    stickyScroll: { enabled: true },

    // Functionality enhancements
    quickSuggestions: true,
    parameterHints: { enabled: true },
    autoClosingBrackets: "always" as const,
    autoClosingQuotes: "always" as const,
    autoClosingTags: true,
    folding: true,
    showFoldingControls: "always" as const,
    foldingHighlight: true,
    matchBrackets: "always" as const,
    linkedEditing: true,
    hover: { enabled: true, delay: 300 },
    suggest: {
      showWords: true,
      showSnippets: true,
      snippetsPreventQuickSuggestions: false,
    },

    scrollbar: {
      verticalScrollbarSize: 10,
      horizontalScrollbarSize: 10,
      useShadows: true,
    },
  };

  cssEditorOptions = {
    theme: "vs-dark",
    language: "css",
    automaticLayout: true,
    wordWrap: "on" as const,
    fontSize: 13,
    lineNumbers: "on" as const,
    tabSize: 2,
    formatOnPaste: true,
    formatOnType: true,
    scrollBeyondLastLine: false,
    padding: { top: 8, bottom: 8 },

    // Visual enhancements
    minimap: { enabled: true, scale: 1, showSlider: "mouseover" as const },
    bracketPairColorization: { enabled: true },
    guides: {
      bracketPairs: true,
      indentation: true,
      highlightActiveIndentation: true,
    },
    renderLineHighlight: "all" as const,
    renderWhitespace: "selection" as const,
    smoothScrolling: true,
    cursorBlinking: "smooth" as const,
    cursorSmoothCaretAnimation: "on" as const,
    colorDecorators: true,
    stickyScroll: { enabled: true },

    // Functionality enhancements
    quickSuggestions: true,
    parameterHints: { enabled: true },
    autoClosingBrackets: "always" as const,
    autoClosingQuotes: "always" as const,
    folding: true,
    showFoldingControls: "always" as const,
    foldingHighlight: true,
    matchBrackets: "always" as const,
    hover: { enabled: true, delay: 300 },
    suggest: {
      showWords: true,
      showSnippets: true,
      snippetsPreventQuickSuggestions: false,
    },

    scrollbar: {
      verticalScrollbarSize: 10,
      horizontalScrollbarSize: 10,
      useShadows: true,
    },
  };

  get isEditMode(): boolean {
    return this._component !== null;
  }

  ngOnInit(): void {
    this.initForm();
    this.setupPreviewUpdates();

    // If component was set before form was initialized, populate it now
    if (this._component) {
      this.populateForm(this._component);
    }
  }

  private initForm(): void {
    this.form = new FormGroup<EditorForm>({
      name: new FormControl("", {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(100)],
      }),
      htmlContent: new FormControl("", {
        nonNullable: true,
        validators: [Validators.required],
      }),
      cssContent: new FormControl("", { nonNullable: true }),
      enabled: new FormControl(true, { nonNullable: true }),
      position: new FormControl(this.calculateLastPosition(), {
        nonNullable: true,
      }),
    });
  }

  /**
   * Calculate the last position for new components (goes at the end)
   */
  private calculateLastPosition(): number {
    const standardMax = this.existingComponents.length;
    const customMax =
      this.customComponents.length > 0
        ? Math.max(...this.customComponents.map((c) => c.order + 1))
        : 0;
    return Math.max(standardMax, customMax);
  }

  private populateForm(component: CustomComponent): void {
    this.form.patchValue({
      name: component.name,
      htmlContent: component.htmlContent,
      cssContent: component.cssContent,
      enabled: component.enabled,
      position: component.order,
    });
    this.updatePreview();
  }

  private resetForm(): void {
    this.form.reset({
      name: "",
      htmlContent: "",
      cssContent: "",
      enabled: true,
      position: this.calculateLastPosition(),
    });
    this.previewSrcdoc = null;
  }

  private setupPreviewUpdates(): void {
    // Update preview when HTML changes
    this.form.controls.htmlContent.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.updatePreview();
      });

    // Update preview when CSS changes
    this.form.controls.cssContent.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.updatePreview();
      });
  }

  updatePreview(): void {
    const htmlContent = this.form.controls.htmlContent.value;
    const cssContent = this.form.controls.cssContent.value;

    if (!htmlContent?.trim()) {
      this.previewSrcdoc = null;
      return;
    }

    // Build a complete HTML document for the sandboxed iframe
    // Scripts run safely because sandbox="allow-scripts" isolates them
    const rawHtml = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>
    /* Reset and base styles */
    *, *::before, *::after { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
      font-size: 14px;
      line-height: 1.5;
      color: #333;
      padding: 1rem;
    }
    img { max-width: 100%; height: auto; display: block; }
    /* User CSS */
    ${cssContent || ""}
  </style>
</head>
<body>
  ${htmlContent}
  <script>
    // Auto-resize iframe to fit content
    function notifyParentOfHeight() {
      var height = Math.max(
        document.body.scrollHeight,
        document.body.offsetHeight,
        document.documentElement.scrollHeight,
        document.documentElement.offsetHeight
      );
      window.parent.postMessage({ type: 'qmc-preview-resize', height: height }, '*');
    }
    window.addEventListener('load', function() { setTimeout(notifyParentOfHeight, 100); });
    notifyParentOfHeight();
    var observer = new MutationObserver(function() { setTimeout(notifyParentOfHeight, 50); });
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });
    window.addEventListener('resize', notifyParentOfHeight);
    document.querySelectorAll('img').forEach(function(img) {
      if (img.complete) notifyParentOfHeight();
      else {
        img.addEventListener('load', notifyParentOfHeight);
        img.addEventListener('error', notifyParentOfHeight);
      }
    });
  </script>
</body>
</html>`;

    // Bypass Angular sanitization - security is enforced by iframe sandbox
    this.previewSrcdoc = this.sanitizer.bypassSecurityTrustHtml(rawHtml);
  }

  /**
   * Get the width for the selected device size
   */
  getDeviceWidth(): string {
    const device = this.deviceSizes.find((d) => d.name === this.selectedDevice);
    return device?.width || "100%";
  }

  /**
   * Handle preview iframe load and auto-resize
   * Note: srcdoc iframes have a "null" origin, so traditional origin validation doesn't apply.
   * Security is ensured by the sandbox attribute and message type validation.
   */
  onPreviewIframeLoad(event: Event): void {
    const iframe = event.target as HTMLIFrameElement;

    // Clean up any existing handler before creating a new one
    if (this.previewMessageHandler) {
      window.removeEventListener("message", this.previewMessageHandler);
    }

    this.previewMessageHandler = (msg: MessageEvent) => {
      // Validate message structure - only accept our specific resize messages
      // Note: srcdoc iframes have origin "null", so we validate by message type instead
      if (
        msg.data?.type === "qmc-preview-resize" &&
        typeof msg.data.height === "number"
      ) {
        // Add buffer, cap at reasonable max
        const contentHeight = msg.data.height + 10;
        const maxHeight = 500;
        iframe.style.height = Math.min(contentHeight, maxHeight) + "px";
      }
    };

    window.addEventListener("message", this.previewMessageHandler);
    iframe.style.height = "200px";
  }

  /**
   * Clean up message listener on component destroy to prevent memory leaks
   */
  ngOnDestroy(): void {
    if (this.previewMessageHandler) {
      window.removeEventListener("message", this.previewMessageHandler);
      this.previewMessageHandler = undefined;
    }
  }

  onSave(): void {
    if (this.form.invalid) return;

    // Save raw HTML/CSS without sanitization
    // Security is handled by rendering in a sandboxed iframe in V2 Portal
    // The sandbox="allow-scripts" attribute isolates scripts from parent page
    const formData: CustomComponentFormData = {
      name: this.form.controls.name.value.trim(),
      htmlContent: this.form.controls.htmlContent.value,
      cssContent: this.form.controls.cssContent.value,
      enabled: this.form.controls.enabled.value,
      order: this.form.controls.position.value,
    };

    this.save.emit(formData);
    this.closeDialog();
  }

  onCancel(): void {
    this.cancelled.emit();
    this.closeDialog();
  }

  private closeDialog(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.resetForm();
  }
}
